import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ColorHash from 'color-hash';

import moment from 'moment';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import CheckboxField from 'components/fields/CheckboxField';
import TextField from 'components/fields/TextField';
import Typography from '@material-ui/core/Typography';
import IconButton from 'components/common/IconButton';
import IconCheckboxSelect from 'components/icons/IconCheckboxSelect';
import IconCheckboxChecked from 'components/icons/IconCheckboxChecked';

import MultiSegmentProgressBar from 'components/common/MultiSegmentProgressBar';

import { Field, Fields, reduxForm } from 'redux-form';

import formatPrice from 'utils/formatPrice';
import * as validate from 'utils/validate';

import dateFormat from 'config/dateFormat';

const colorHash = new ColorHash();

const styles = theme => ({
  root: {
    paddingRight: 16,
    paddingBottom: 8,
  },
  checkboxField: {
    display: 'flex',
    marginLeft: 0,
    marginTop: 0,
    marginBottom: 0,
  },
  checkboxFieldLabel: {
    display: 'flex',
    flexShrink: 1,
    flexBasis: '100%',
    width: '100%',
    flexDirection: 'column',
  },
  checkboxFieldLabelText: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  buttonDeselect: {
    color: theme.palette.primary[500],
  },
  iconCheckboxChecked: {
    width: 18,
    height: 18,
  },
  iconCheckboxSelect: {
    width: 18,
    height: 18,
  },
  selectAllContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: -4,
  },
  filtersContainer: {
    margin: '16px 0 0 8px',
  },
  typographyButtonLabel: {
    textTransform: 'uppercase',
  },
  iconColorHash: {
    display: 'inline-block',
    width: 8,
    height: 8,
    marginRight: 4,
    borderRadius: 8,
    verticalAlign: 'middle',
  },
});

const validateForm = (values) => {
  const errors = {};

  if (values.State && values.State !== values['Selected State']) {
    errors.State = 'Invalid State';
  }

  if (values.Status && values.Status !== values['Selected Status']) {
    errors.Status = 'Invalid Status';
  }

  if (!values['Date Range'] && !values['Date From']) {
    errors['Date From'] = 'Field is required';
  }

  if (!values['Date Range'] && !values['Date To']) {
    errors['Date To'] = 'Field is required';
  }

  if (values['Date From'] && !errors['Date From']) {
    errors['Date From'] = validate.date(values['Date From']);
  }

  if (values['Date To'] && !errors['Date To']) {
    errors['Date To'] = validate.date(values['Date To']);
  }

  if (values['Date From']
    && !validate.date(values['Date From'])
    && values['Date To']
    && !validate.date(values['Date To'])
    && moment(values['Date To'], dateFormat).isBefore(moment(values['Date From'], dateFormat))) {
    errors['Date To'] = 'Date To should be after Date From';
  }

  return errors;
};

const EraChartPayersForm = ({
  handleSubmit,
  classes,
  theme,
  formValues,
  change,
  payers,
}) => {
  const allPayersSelected = Object.keys(formValues).filter(key => 
    formValues[key] && !['Payer Name'].includes(key)).length === payers.length;
  const filteredPayers = formValues['Payer Name'] && formValues['Payer Name'].length > 0 ? payers.filter(payer =>
    payer.Name.toLowerCase().includes(formValues['Payer Name'].toLowerCase())) : payers;
  return (
    <form
      className={classes.root}
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit}
    >
      <Grid
        container
        justify="space-between"
        spacing={0}
        direction="column"
      >
        <Grid item>
          <div className={classes.filtersContainer}>
            <Grid
              container
              justify="space-between"
              spacing={8}
              direction="row"
              wrap="nowrap"
            >
              <Grid item xs={12}>
                <Field
                  id="Payer Name"
                  name="Payer Name"
                  placeholder="Filter by Payer Name"
                  component={TextField}
                  fullWidth
                  size="sm"
                />
              </Grid>
            </Grid>
          </div>
        </Grid>
        {payers.length > 0 && (
          <Grid item>
            <div className={classes.selectAllContainer}>
              <IconButton
                className={classNames({
                  [classes.buttonDeselect]: allPayersSelected,
                })}
                {...!allPayersSelected
                  ? {
                    onClick: () => {
                      payers.forEach(payer => {
                        change(payer.Name, true);
                      });
                    },
                  } : {
                    onClick: () => {
                      payers.forEach(payer => {
                        change(payer.Name, false);
                      });
                    },
                  }}
                tooltipContent={!allPayersSelected ? 'Select all' : 'Deselect all'}
              >
                {!allPayersSelected ? (
                  <IconCheckboxSelect className={classes.iconCheckboxSelect} />
                ) : (
                  <IconCheckboxChecked className={classes.iconCheckboxChecked} />
                )}
              </IconButton>
              <Typography
                className={classes.typographyButtonLabel}
                variant="subheading"
                color="secondary"
              >
                {!allPayersSelected ? 'Select all' : 'Deselect all'}
              </Typography>
            </div>
          </Grid>
        )}
        {filteredPayers.map(payer => (
          <Grid item key={payer.Name}>
            <Field
              className={classes.checkboxField}
              id={`${payer.Name}`}
              name={`${payer.Name}`}
              labelClassName={classes.checkboxFieldLabel}
              label={(
                <Fragment>
                  <div className={classes.checkboxFieldLabelText}>
                    <Typography>
                      <div
                        className={classes.iconColorHash}
                        style={{ backgroundColor: colorHash.hex(`00000${payer.Name}`.slice(-6)) }}
                      />
                      {payer.Name}
                    </Typography>
                    <Typography>
                      {formatPrice(payer['Paid Reimbs'])}
                    </Typography>
                  </div>
                  <MultiSegmentProgressBar
                    segments={[
                      {
                        value: payer['Paid Claims Count'],
                        color: theme.palette.states.paid,
                      },
                      {
                        value: payer['Partial Claims Count'],
                        color: theme.palette.states.intake,
                      },
                      {
                        value: payer['Denied Claims Count'],
                        color: theme.palette.states.rejected,
                      },
                    ]}
                    height={4}
                    gap={1}
                    borderRadius={4}
                    showLabels
                  />
                </Fragment>
              )}
              component={CheckboxField}
            />
          </Grid>
        ))}
      </Grid>
    </form>
  );
};

EraChartPayersForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
  }).isRequired,
};

export default reduxForm({
  validate: validateForm,
})(withStyles(styles, { withTheme: true })(EraChartPayersForm));



// WEBPACK FOOTER //
// ./src/components/home/EraChart/EraChartPayersForm.js